import type { ReactNode } from 'react'

import { cx } from './cx'

/*
 | Las tres cajas que aparecen una y otra vez: tarjeta, insignia y aviso.
 |
 | Van juntas en un archivo porque comparten la misma decisión —cómo se separa
 | algo del fondo— y separarlas en tres ficheros de veinte líneas sólo añade
 | imports.
 */

/* -------------------------------------------------------------------------- */

interface CardProps {
  children: ReactNode
  /** Resalta la tarjeta: el plan actual, el más elegido. */
  highlighted?: boolean
  /** Reacciona al puntero. Para tarjetas que son un enlace. */
  interactive?: boolean
  className?: string
}

/**
 * La elevación se dibuja con borde Y sombra a la vez.
 *
 * En el tema oscuro la sombra casi no existe (`--shadow-strength` baja) y quien
 * separa la tarjeta es el borde; en los claros manda la sombra y el borde es
 * casi invisible. Con una sola regla, los dos temas quedan bien.
 */
export function Card({ children, highlighted, interactive, className }: CardProps) {
  return (
    <div
      className={cx(
        'rounded-card border bg-surface-raised shadow-raised',
        highlighted === true ? 'border-accent ring-1 ring-accent' : 'border-line',
        interactive === true && 'transition-colors hover:border-accent',
        className,
      )}
    >
      {children}
    </div>
  )
}

/* -------------------------------------------------------------------------- */

export type Tone = 'neutral' | 'accent' | 'positive' | 'warning' | 'danger'

const BADGE_TONES: Record<Tone, string> = {
  neutral: 'bg-surface-sunken text-ink-muted',
  accent: 'bg-accent-subtle text-accent',
  positive: 'bg-positive-subtle text-positive',
  warning: 'bg-warning-subtle text-warning',
  danger: 'bg-danger-subtle text-danger',
}

export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode
  tone?: Tone
  className?: string
}) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-caption font-medium',
        BADGE_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

/* -------------------------------------------------------------------------- */

const BANNER_TONES: Record<Tone, string> = {
  neutral: 'border-line bg-surface-sunken',
  accent: 'border-accent/30 bg-accent-subtle',
  positive: 'border-positive-line bg-positive-subtle',
  warning: 'border-warning-line bg-warning-subtle',
  danger: 'border-danger-line bg-danger-subtle',
}

interface BannerProps {
  tone?: Tone
  title?: string
  children: ReactNode
  /** Acción al pie: normalmente el enlace que resuelve el aviso. */
  action?: ReactNode
  /**
   * `status` se anuncia sin interrumpir; `alert` interrumpe.
   *
   * Un impago se anuncia (`status`); un error del formulario que acaba de
   * enviarse interrumpe (`alert`). Usar `alert` para todo hace que un lector de
   * pantalla corte al usuario a media frase por un aviso que no urge.
   */
  live?: 'status' | 'alert'
  className?: string
}

export function Banner({
  tone = 'neutral',
  title,
  children,
  action,
  live = 'status',
  className,
}: BannerProps) {
  return (
    <div
      role={live}
      className={cx('rounded-card border px-4 py-3', BANNER_TONES[tone], className)}
    >
      {title !== undefined && <p className="text-label font-semibold text-ink">{title}</p>}
      <div className={cx('text-body text-ink-muted', title !== undefined && 'mt-1')}>{children}</div>
      {action !== undefined && <div className="mt-3">{action}</div>}
    </div>
  )
}
